"use client";

/**
 * Временный общий чат тестеров — плавающий виджет слева внизу.
 *
 * Кнопка с иконкой сообщения и бейджем непрочитанных; по клику открывается панель
 * с общей лентой. Опрос новых сообщений через SWR (чаще когда открыт). «Непрочитано»
 * = сообщения новее последнего просмотренного id (хранится в localStorage, читается
 * только в useEffect — SSR-safe). Не авторизован (401) — виджет скрыт. Когда чат
 * больше не нужен, удаляются: этот компонент, /api/chat, миграция 108, монтаж в layout.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { MessageCircle, X, Send } from "lucide-react";

type ChatMessage = { id: number; author: string; text: string; createdAt: number; isMine: boolean };
type ChatResponse = { messages?: ChatMessage[]; unauthorized?: boolean };

const LAST_SEEN_KEY = "frogstat_user_chat_last_seen";

const fetcher = async (url: string): Promise<ChatResponse> => {
  const res = await fetch(url, { headers: { "Cache-Control": "no-store" } });
  if (res.status === 401) return { unauthorized: true };
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
};

function formatTime(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

const SEPARATOR_MONTHS = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
];

function isSameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}

// Подпись-разделитель по центру ленты (как в мессенджерах): «Сегодня» / «Вчера»
// / «9 июня», с годом, если сообщение из прошлого года. Показывается над первым
// сообщением каждого нового дня — чтобы было видно, что переписка старая.
function formatDateSeparator(ms: number): string {
  const d = new Date(ms);
  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 0) return "Сегодня";
  if (diffDays === 1) return "Вчера";
  const year = d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : "";
  return `${d.getDate()} ${SEPARATOR_MONTHS[d.getMonth()]}${year}`;
}

export default function UserChat() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [lastSeen, setLastSeen] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const { data, mutate } = useSWR<ChatResponse>("/api/chat", fetcher, {
    refreshInterval: open ? 3000 : 8000,
    revalidateOnFocus: true,
  });

  const messages = data?.messages ?? [];
  const unauthorized = data?.unauthorized === true;

  // SSR-safe: читаем последний просмотренный id только в браузере.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(LAST_SEEN_KEY);
      if (raw) setLastSeen(Number(raw) || 0);
    } catch { /* ignore */ }
  }, []);

  const maxId = useMemo(() => messages.reduce((m, x) => Math.max(m, x.id), 0), [messages]);
  const unread = useMemo(
    () => messages.filter(m => m.id > lastSeen && !m.isMine).length,
    [messages, lastSeen],
  );

  // Открыли панель или пришли новые при открытой — помечаем всё прочитанным.
  useEffect(() => {
    if (!open || maxId <= lastSeen) return;
    setLastSeen(maxId);
    try { window.localStorage.setItem(LAST_SEEN_KEY, String(maxId)); } catch { /* ignore */ }
  }, [open, maxId, lastSeen]);

  // Автопрокрутка вниз при новых сообщениях, пока панель открыта.
  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, maxId]);

  if (unauthorized) return null;

  const send = async () => {
    const value = text.trim();
    if (!value || sending) return;
    setSending(true);
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: value }),
      });
      if (res.ok) {
        setText("");
        await mutate();
      }
    } catch { /* ignore */ } finally {
      setSending(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed bottom-[76px] right-4 z-[59] flex w-[330px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
          style={{ height: "min(460px, calc(100vh - 120px))" }}
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-gray-900">Чат тестеров</span>
              <span className="text-[11px] text-gray-400">Общий чат — видят все участники</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Закрыть чат"
            >
              <X size={16} />
            </button>
          </div>

          <div ref={listRef} className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
            {messages.length === 0 ? (
              <div className="flex h-full items-center justify-center px-6 text-center text-xs text-gray-400">
                Сообщений пока нет. Напишите первым.
              </div>
            ) : (
              messages.map((m, i) => {
                const prev = i > 0 ? messages[i - 1] : null;
                const showDate = !prev || !isSameDay(prev.createdAt, m.createdAt);
                return (
                  <Fragment key={m.id}>
                    {showDate && (
                      <div className="flex justify-center py-1.5">
                        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[10px] font-medium text-gray-500">
                          {formatDateSeparator(m.createdAt)}
                        </span>
                      </div>
                    )}
                    <div className={`flex flex-col ${m.isMine ? "items-end" : "items-start"}`}>
                      <div
                        className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm ${
                          m.isMine ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-900"
                        }`}
                      >
                        {!m.isMine && <div className="mb-0.5 text-[11px] font-medium opacity-70">{m.author}</div>}
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                      </div>
                      <span className="mt-0.5 px-1 text-[10px] text-gray-400">{formatTime(m.createdAt)}</span>
                    </div>
                  </Fragment>
                );
              })
            )}
          </div>

          <div className="flex items-end gap-2 border-t border-gray-100 p-2">
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              rows={1}
              maxLength={2000}
              placeholder="Сообщение..."
              className="max-h-28 min-h-[38px] flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400"
            />
            <button
              onClick={() => void send()}
              disabled={!text.trim() || sending}
              className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white transition-colors hover:bg-indigo-700 disabled:opacity-40"
              aria-label="Отправить"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-4 right-4 z-[60] flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition-colors hover:bg-indigo-700"
        aria-label={open ? "Свернуть чат" : "Открыть чат тестеров"}
        title="Чат тестеров"
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
        {!open && unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
    </>
  );
}
